import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const WorkoutPreviewModal = ({ workout, onClose }) => {
  const navigate = useNavigate();

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  if (!workout) return null;

  const handleStartWorkout = () => {
    navigate('/active-workout', { state: { workout } });
  };

  const getDifficultyColor = (level) => {
    const colors = {
      beginner: 'bg-success/10 text-success border-success/20',
      intermediate: 'bg-warning/10 text-warning border-warning/20',
      advanced: 'bg-error/10 text-error border-error/20'
    };
    return colors?.[level?.toLowerCase()] || colors?.beginner;
  };

  return (
    <div 
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="bg-card rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto overflow-elegant"
        onClick={(e) => e?.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between p-4 md:p-6 bg-card border-b border-border">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground">
            Workout Preview
          </h2>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-10 h-10 rounded-lg hover:bg-muted transition-smooth press-scale"
            aria-label="Close preview"
          >
            <Icon name="X" size={24} color="var(--color-foreground)" />
          </button>
        </div>

        <div className="p-4 md:p-6 lg:p-8">
          <div className="relative aspect-video rounded-lg overflow-hidden mb-6">
            <Image
              src={workout?.image}
              alt={workout?.imageAlt}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
              <button className="flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-accent rounded-full shadow-xl hover:bg-accent/90 transition-smooth hover-lift press-scale">
                <Icon name="Play" size={32} color="var(--color-accent-foreground)" />
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className={`px-3 py-1 rounded-full text-sm font-semibold border caption ${getDifficultyColor(workout?.difficulty)}`}>
              {workout?.difficulty}
            </div>
            <div className="flex items-center gap-1">
              <Icon name="Star" size={16} color="var(--color-warning)" fill="var(--color-warning)" />
              <span className="text-sm font-medium data-text">{workout?.rating}</span>
              <span className="text-sm text-muted-foreground">({workout?.reviews} reviews)</span>
            </div>
          </div>

          <h3 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-3">
            {workout?.title}
          </h3>

          <p className="text-base md:text-lg text-muted-foreground mb-6">
            {workout?.description}
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="flex flex-col items-center p-4 bg-muted rounded-lg">
              <Icon name="Clock" size={24} color="var(--color-primary)" className="mb-2" />
              <span className="text-sm text-muted-foreground mb-1">Duration</span>
              <span className="text-base font-semibold data-text text-foreground">{workout?.duration}</span>
            </div>
            <div className="flex flex-col items-center p-4 bg-muted rounded-lg">
              <Icon name="Dumbbell" size={24} color="var(--color-primary)" className="mb-2" />
              <span className="text-sm text-muted-foreground mb-1">Exercises</span>
              <span className="text-base font-semibold data-text text-foreground">{workout?.exercises}</span>
            </div>
            <div className="flex flex-col items-center p-4 bg-muted rounded-lg">
              <Icon name="Flame" size={24} color="var(--color-accent)" className="mb-2" />
              <span className="text-sm text-muted-foreground mb-1">Calories</span>
              <span className="text-base font-semibold data-text text-foreground">{workout?.calories}</span>
            </div>
            <div className="flex flex-col items-center p-4 bg-muted rounded-lg">
              <Icon name="Users" size={24} color="var(--color-primary)" className="mb-2" />
              <span className="text-sm text-muted-foreground mb-1">Completed</span>
              <span className="text-base font-semibold data-text text-foreground">{workout?.completions}</span>
            </div>
          </div>

          <div className="mb-6">
            <h4 className="text-lg font-heading font-semibold text-foreground mb-3">
              Equipment Needed
            </h4>
            <div className="flex flex-wrap gap-2">
              {workout?.equipment?.map((item, index) => (
                <span
                  key={index}
                  className="px-3 py-2 bg-muted text-foreground rounded-lg text-sm caption"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <h4 className="text-lg font-heading font-semibold text-foreground mb-3">
              Exercise List
            </h4>
            <div className="space-y-3">
              {workout?.exerciseList?.map((exercise, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                  <div className="flex items-center justify-center w-8 h-8 bg-primary text-primary-foreground rounded-full font-bold text-sm data-text flex-shrink-0">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground">{exercise?.name}</p>
                    <p className="text-sm text-muted-foreground">{exercise?.sets}</p>
                  </div>
                  <Icon name="ChevronRight" size={20} color="var(--color-muted-foreground)" />
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="default"
              size="lg"
              iconName="Play"
              iconPosition="left"
              onClick={handleStartWorkout}
              fullWidth
            >
              Start Workout
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="Bookmark"
              iconPosition="left"
              onClick={onClose}
              fullWidth
            >
              Save for Later
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkoutPreviewModal;